import { useId } from 'react'
import { cn } from '@/lib/utils'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import { joinLocalInput, splitLocalInput, toLocalInput } from '@/lib/planningDates'

/**
 * The body of a planned-date picker: a month grid and a time, editing ONE
 * `datetime-local` draft.
 *
 * Shared because two screens pick a planned date against the same rules — the
 * procedures list's Scheduled/Due cells and a process step's expected start —
 * and both previously used a native `datetime-local`, whose popup cannot hold a
 * confirm button (the browser draws it outside the document). Each of them had
 * its own copy of the same comment explaining why applying had to be explicit,
 * which is a good sign the picking belongs in one place.
 *
 * **The footer is deliberately NOT here.** What the actions mean differs: the
 * table cell can clear a date, while a step's expected start applies a plan
 * that re-books machines and words its button accordingly. So this owns the
 * part that must not diverge — the grid, the floor, the draft arithmetic — and
 * each caller owns its own buttons.
 */
export function DateTimePanel({
  value,
  onChange,
  min = null,
  disabled = false,
  blocker = null,
  className,
}: {
  /** The draft, as a `datetime-local` string (`YYYY-MM-DDTHH:mm`). */
  value: string
  onChange: (next: string) => void
  /** Earliest selectable instant, ISO. Whole days before it are unclickable. */
  min?: string | null
  disabled?: boolean
  /**
   * Why the caller's Apply is unavailable, shown under the controls. A disabled
   * button on its own leaves the operator guessing which half is missing.
   */
  blocker?: string | null
  className?: string
}) {
  // Two of these can be on screen at once (Scheduled and Due in one row), so
  // the time field's label needs an id unique per instance, not per page.
  const fieldId = useId()
  const { date: draftDay, time: draftTime } = splitLocalInput(value)

  return (
    <div className={cn('p-3', className)}>
      <Calendar
        mode='single'
        autoFocus
        selected={draftDay ? new Date(`${draftDay}T00:00`) : undefined}
        // Open on the draft's own month, else the floor's, else today. Opening
        // on today when the run is booked two years out means navigating there
        // by hand every single time.
        defaultMonth={draftDay ? new Date(`${draftDay}T00:00`) : min ? new Date(min) : undefined}
        // Whole days only — a calendar cannot express "not before 09:00 today",
        // which is why `isBeforeMin` re-checks the rejoined draft before any
        // caller's Apply will fire.
        disabled={min ? { before: new Date(`${localDay(min)}T00:00`) } : undefined}
        onSelect={day => onChange(day ? joinLocalInput(localDay(day), draftTime) : '')}
      />
      <div className='mt-3 flex items-center gap-2 border-t pt-3'>
        <label htmlFor={fieldId} className='text-xs font-medium text-muted-foreground'>
          Time
        </label>
        <Input
          id={fieldId}
          type='time'
          value={draftTime}
          disabled={disabled}
          onChange={e => onChange(joinLocalInput(draftDay, e.target.value))}
          className='h-7 w-[7.5rem] px-2 text-xs'
        />
      </div>
      {blocker && <p className='mt-2 text-[11px] text-muted-foreground'>{blocker}</p>}
    </div>
  )
}

/**
 * The LOCAL calendar day of an instant, spelled `YYYY-MM-DD`.
 *
 * Composed from the tested helpers rather than formatted here, so the day the
 * calendar highlights is derived exactly the way the draft is written. The
 * local part matters: `new Date('2027-01-01')` is UTC midnight and reads as
 * 31 December anywhere west of Greenwich, lighting up the wrong cell.
 */
export const localDay = (at: string | Date): string =>
  splitLocalInput(toLocalInput(typeof at === 'string' ? at : at.toISOString())).date
